// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Spinner } from '@loa/ui-components';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { relationshipApi } from '../api/relationshipApi';
import { policyApi } from '../api/policyApi';
import { clientApi } from '../api/clientApi';
import { profileRequestApi } from '../api/profileRequestApi';
import { emailPreviewApi } from '../api/emailPreviewApi';
import { invitationApi } from '../api/invitationApi';
import { EmailComposerPanel } from '../components/email/EmailComposerPanel';
import type { Relationship } from '../types/relationship';
import type { Policy } from '../types/policy';
import type { Client } from '../types/client';
import {
  NewRequestCard,
  type NewRequestDraft,
} from '../components/requests/NewRequestCard';
import { getTypeSpec } from '../components/requests/requestTypeRegistry';

function makeDraft(): NewRequestDraft {
  return {
    id: `card-${Math.random().toString(36).slice(2, 10)}`,
    requestType: '',
    policyId: '',
    targetClientId: '',
    params: {},
  };
}

interface CardResult {
  id: string;
  status: 'pending' | 'success' | 'error';
  error?: string | null;
  /**
   * ProfileRequest id returned from the backend once the create call has
   * succeeded. Collected into the consolidated invitation payload at the
   * composer step.
   */
  requestId?: string;
}

type Step = 'cards' | 'composer';

function validate(draft: NewRequestDraft): string | null {
  if (!draft.requestType) return 'Choose a request type';
  const spec = getTypeSpec(draft.requestType);
  if (!spec) return 'Unsupported request type';
  if (spec.policyCardinality === 'EXACTLY_ONE' && !draft.policyId) {
    return 'Choose a policy';
  }
  if (spec.clientCardinality === 'EXACTLY_ONE' && !draft.targetClientId) {
    return 'Choose a client';
  }
  for (const field of spec.additionalFields) {
    if (field.required && !draft.params[field.key]?.trim()) {
      return `${field.label} is required`;
    }
  }
  return null;
}

function extractErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const data = (err as { response?: { data?: { message?: string } } }).response?.data;
    if (data?.message) return data.message;
  }
  if (err instanceof Error) return err.message;
  return 'Failed to create request. Please try again.';
}

/**
 * Route component for {@code /relationships/:id/requests/new} — the
 * "New Request" wizard shown in the advisor UI Figma
 * (DJMqQ4KZPyK2L5P6nM52A6 → 428:54724 / 428:64519).
 *
 * <p>Holds a client-side list of <em>draft</em> Request Cards. Each card
 * lets the advisor pick a request type and (when the type's policy
 * cardinality is {@code EXACTLY_ONE}) the target policy. Clicking
 * <strong>Send Emails</strong> dispatches one create call per card using
 * the existing legacy endpoints (<code>/api/loa</code> and
 * <code>/api/coa</code>) which already dual-write a {@code ProfileRequest}
 * row and trigger the per-type client invitation email. On success the
 * page navigates back to the Requests tab with
 * {@code ?toast=requests-sent} so the tab can render the success banner.
 *
 * <p>A future PR will replace the per-type auto-emails with a single
 * consolidated invitation and insert an <code>EmailComposerPanel</code>
 * step between Send Emails and the final navigation — see the plan at
 * {@code ~/.claude/plans/study-docs-specs-change-of-agency-prd-md-valiant-bee.md}.
 */
export function NewRequestPage() {
  const { id: profileId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setBreadcrumbItems, setBackTo, setBreadcrumbTitle } = usePageHeader();

  const [relationship, setRelationship] = useState<Relationship | null>(null);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [primaryClient, setPrimaryClient] = useState<Client | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [drafts, setDrafts] = useState<NewRequestDraft[]>(() => [makeDraft()]);
  const [results, setResults] = useState<Map<string, CardResult>>(new Map());
  const [sending, setSending] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);

  // Composer step
  const [step, setStep] = useState<Step>('cards');
  const [composerSubject, setComposerSubject] = useState('');
  const [composerBody, setComposerBody] = useState('');
  const [composerInitialBody, setComposerInitialBody] = useState('');
  const [composerInitialSubject, setComposerInitialSubject] = useState('');
  const [composerLoading, setComposerLoading] = useState(false);
  const [composerError, setComposerError] = useState<string | null>(null);
  const [createdRequestIds, setCreatedRequestIds] = useState<string[]>([]);

  // Load profile + policies + primary client
  useEffect(() => {
    if (!profileId) {
      setLoadError('Missing profile id');
      setLoadingData(false);
      return;
    }
    let cancelled = false;
    setLoadingData(true);
    (async () => {
      try {
        const [rel, pols, profileClients] = await Promise.all([
          relationshipApi.getRelationship(profileId),
          policyApi.getPoliciesByProfile(profileId),
          clientApi.getClientsByProfile(profileId),
        ]);
        if (cancelled) return;
        setRelationship(rel);
        setPolicies(pols);
        setClients(profileClients);
        setPrimaryClient(profileClients.find((c) => c.isPrimary) ?? profileClients[0] ?? null);
      } catch (err) {
        if (cancelled) return;
        setLoadError(extractErrorMessage(err));
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  // Breadcrumb
  useEffect(() => {
    if (!relationship) return;
    const label =
      relationship.relationshipName || relationship.profileName || 'Relationship';
    setBreadcrumbItems([
      { label: 'Relationships', to: '/relationships' },
      { label, to: `/relationships/${relationship.id}` },
      { label: 'New Request' },
    ]);
    setBackTo(`/relationships/${relationship.id}?tab=requests`);
    setBreadcrumbTitle(null);
    return () => {
      setBreadcrumbItems(null);
      setBackTo(null);
    };
  }, [
    relationship,
    setBreadcrumbItems,
    setBackTo,
    setBreadcrumbTitle,
  ]);

  const handleUpdateDraft = useCallback((id: string, next: NewRequestDraft) => {
    setDrafts((prev) => prev.map((d) => (d.id === id ? next : d)));
    setResults((prev) => {
      // Dropping any prior per-card error so the card looks clean again after
      // the advisor edits the inputs.
      if (!prev.has(id)) return prev;
      const copy = new Map(prev);
      copy.delete(id);
      return copy;
    });
  }, []);

  const handleDeleteDraft = useCallback((id: string) => {
    setDrafts((prev) => prev.filter((d) => d.id !== id));
    setResults((prev) => {
      if (!prev.has(id)) return prev;
      const copy = new Map(prev);
      copy.delete(id);
      return copy;
    });
  }, []);

  const handleAddAnother = useCallback(() => {
    setDrafts((prev) => [...prev, makeDraft()]);
  }, []);

  const canSend = useMemo(() => {
    if (drafts.length === 0) return false;
    return drafts.every((d) => validate(d) === null);
  }, [drafts]);

  const handleSendEmails = useCallback(async () => {
    if (!relationship || !profileId || !primaryClient) return;
    setSending(true);
    setGlobalError(null);
    const nextResults = new Map(results);
    const newRequestIds: string[] = [...createdRequestIds];
    let firstFailure: string | null = null;
    try {
      for (const draft of drafts) {
        if (nextResults.get(draft.id)?.status === 'success') continue;
        const validationError = validate(draft);
        if (validationError) {
          nextResults.set(draft.id, { id: draft.id, status: 'error', error: validationError });
          firstFailure ??= validationError;
          continue;
        }

        try {
          // Single create path for every request type — POST /api/requests
          // is the front door now. The LoaRequestHandler / CoaRequestHandler
          // on the resource server owns detail-entity creation (LOA PDF +
          // envelope, CoA PDF + envelope) under the same transaction. The
          // response carries the ProfileRequest id which we collect here to
          // drive the consolidated invitation email below.
          const spec = getTypeSpec(draft.requestType as 'LOA' | 'COA' | 'OPEN_BANKING_CONNECTION');
          const created = await profileRequestApi.create({
            profileId,
            requestType: draft.requestType as 'LOA' | 'COA' | 'OPEN_BANKING_CONNECTION',
            policyId: spec?.policyCardinality === 'EXACTLY_ONE' ? draft.policyId : null,
            initiatedByType: 'ADVISOR',
            typeSpecificInput:
              spec?.clientCardinality === 'EXACTLY_ONE'
                ? { targetClientId: draft.targetClientId }
                : undefined,
          });
          newRequestIds.push(created.id);
          nextResults.set(draft.id, { id: draft.id, status: 'success', requestId: created.id });
        } catch (err) {
          const message = extractErrorMessage(err);
          nextResults.set(draft.id, { id: draft.id, status: 'error', error: message });
          firstFailure ??= message;
        }
      }
      setResults(nextResults);
      setCreatedRequestIds(newRequestIds);

      if (firstFailure) {
        const succeededCount = Array.from(nextResults.values()).filter((r) => r.status === 'success').length;
        setGlobalError(
          `${succeededCount} of ${drafts.length} requests created. Fix the remaining cards and try again.`,
        );
        return;
      }

      // All cards created → fetch preview and flip to composer step.
      setComposerLoading(true);
      setComposerError(null);
      try {
        const preview = await emailPreviewApi.preview({
          kind: 'CLIENT_REQUESTS_INVITATION',
          profileId,
          clientId: primaryClient.id,
          requestIds: newRequestIds,
        });
        setComposerSubject(preview.subject);
        setComposerInitialSubject(preview.subject);
        setComposerBody(preview.editableBodyHtml);
        setComposerInitialBody(preview.editableBodyHtml);
        setStep('composer');
      } catch (err) {
        setComposerError(extractErrorMessage(err));
      } finally {
        setComposerLoading(false);
      }
    } finally {
      setSending(false);
    }
  }, [drafts, profileId, primaryClient, relationship, results, createdRequestIds]);

  const handleComposerSend = useCallback(async () => {
    if (!profileId || !primaryClient) return;
    setComposerLoading(true);
    setComposerError(null);
    try {
      await invitationApi.sendRequestsInvitation(profileId, primaryClient.id, {
        customSubject:
          composerSubject === composerInitialSubject ? undefined : composerSubject,
        customBody:
          composerBody === composerInitialBody ? undefined : composerBody,
        requestIds: createdRequestIds,
      });
      navigate(`/relationships/${profileId}?tab=requests&toast=requests-sent`);
    } catch (err) {
      setComposerError(extractErrorMessage(err));
    } finally {
      setComposerLoading(false);
    }
  }, [
    profileId, primaryClient, composerSubject, composerInitialSubject,
    composerBody, composerInitialBody, createdRequestIds, navigate,
  ]);

  const handleComposerCancel = useCallback(() => {
    // Requests are already created; cancelling just navigates away without
    // sending the consolidated email. The per-type emails were suppressed,
    // so the client won't be notified until the advisor returns and sends.
    if (profileId) {
      navigate(`/relationships/${profileId}?tab=requests`);
    }
  }, [navigate, profileId]);

  const composerIsModified =
    composerSubject !== composerInitialSubject
    || composerBody !== composerInitialBody;

  const handleComposerReset = useCallback(() => {
    setComposerSubject(composerInitialSubject);
    setComposerBody(composerInitialBody);
  }, [composerInitialSubject, composerInitialBody]);

  if (loadingData) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Spinner />
      </div>
    );
  }

  if (loadError || !relationship) {
    return (
      <div className="p-6">
        <Alert variant="error" header="Could not load relationship">
          {loadError ?? 'Relationship not found'}
        </Alert>
      </div>
    );
  }

  if (step === 'composer') {
    // Render the composer full-width, matching the Nudge Client / Nudge
    // Provider pages (EmailComposerPanel is already w-full + flex-1). The
    // panel renders its own error banner via the `error` prop.
    return (
      <EmailComposerPanel
        subject={composerSubject}
        onSubjectChange={setComposerSubject}
        subjectPlaceholder="Subject"
        recipients={primaryClient ? `${primaryClient.firstName} ${primaryClient.lastName} <${primaryClient.email}>` : ''}
        body={composerBody}
        onBodyChange={setComposerBody}
        isModified={composerIsModified}
        onReset={handleComposerReset}
        onCancel={handleComposerCancel}
        onSend={handleComposerSend}
        sendLabel="Send Email"
        sending={composerLoading}
        error={composerError}
      />
    );
  }

  return (
    <div className="bg-page-bg flex-1 p-lg">
      <div className="flex flex-col gap-lg">
        {globalError && (
          <Alert variant="warning" header="Partial send">
            {globalError}
          </Alert>
        )}

        {drafts.map((draft) => {
          const result = results.get(draft.id);
          return (
            <NewRequestCard
              key={draft.id}
              draft={draft}
              policies={policies}
              clients={clients}
              onChange={(next) => handleUpdateDraft(draft.id, next)}
              onDelete={() => handleDeleteDraft(draft.id)}
              disabled={sending || result?.status === 'success'}
              error={result?.status === 'error' ? result.error : null}
              showDelete={drafts.length > 1}
            />
          );
        })}

        <div className="flex items-center justify-end gap-md">
          <Button
            variant="outlined"
            size="lg"
            onClick={handleAddAnother}
            disabled={sending}
            data-testid="new-request-add-another"
          >
            Add Another Request
          </Button>
          <Button
            variant="filled"
            size="lg"
            onClick={handleSendEmails}
            disabled={!canSend || sending}
            loading={sending}
            data-testid="new-request-send-emails"
          >
            Send Emails
          </Button>
        </div>
      </div>
    </div>
  );
}

export default NewRequestPage;
